import { ordinal } from '../../utils/format';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Autocomplete,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { RootState, AppDispatch } from '../../store/store';
import { fetchBills, setFilters } from '../../store/slices/billsSlice';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import ExportButtons from '../../components/Common/ExportButtons';

// Helper: normalize bill id input e.g., "H.R. 1234", "hr1234", "H Res 55" -> { type: 'hr', number: '1234' }
function normalizeBillId(input: string): { type: string; number: string } | null {
  if (!input) return null;
  const s = input.trim().toLowerCase().replace(/\./g, '').replace(/-/g, ' ').replace(/\s+/g, ' ');
  // map common names to codes
  const typeMap: Record<string, string> = {
    hr: 'hr', 'h r': 'hr', 'hres': 'hres', 'h res': 'hres', 'hjres': 'hjres', 'h j res': 'hjres', 'hjr': 'hjres',
    hconres: 'hconres', 'h con res': 'hconres', s: 's', 'sres': 'sres', 's res': 'sres', sjres: 'sjres', 's j res': 'sjres',
    sconres: 'sconres', 's con res': 'sconres'
  };
  const match = s.match(/^(hr|h r|hres|h res|hjres|h j res|hjr|hconres|h con res|s|sres|s res|sjres|s j res|sconres|s con res)\s*(\d{1,5})$/);
  if (!match) return null;
  const typeKey = match[1];
  const number = match[2];
  const type = typeMap[typeKey] || typeKey.replace(' ', '');
  return { type, number };
}

// Helper: format bill id, e.g., HR 1234 -> H.R. 1234 or HR-1234 for URL
function formatBillId(type: string, number: string, style: 'pretty' | 'url' = 'pretty'): string {
  const t = type.toUpperCase();
  if (style === 'url') return `${t}-${number}`;
  const dotTypes = new Set(['HR', 'S']);
  return dotTypes.has(t) ? `${t[0]}. ${t[1]}. ${number}`.replace('  ', '. ') : `${t} ${number}`;
}

const typeMeta: Record<string, { label: string; desc: string; color: 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error' }> = {
  hr: { label: 'HR', desc: 'House Bill', color: 'primary' },
  hres: { label: 'HRES', desc: 'House Resolution', color: 'info' },
  hjres: { label: 'HJRES', desc: 'House Joint Resolution', color: 'warning' },
  hconres: { label: 'HCONRES', desc: 'House Concurrent Resolution', color: 'secondary' },
  s: { label: 'S', desc: 'Senate Bill', color: 'success' },
  sres: { label: 'SRES', desc: 'Senate Resolution', color: 'info' },
  sjres: { label: 'SJRES', desc: 'Senate Joint Resolution', color: 'warning' },
  sconres: { label: 'SCONRES', desc: 'Senate Concurrent Resolution', color: 'secondary' },
  hamdt: { label: 'HAMDT', desc: 'House Amendment (H.Amdt.)', color: 'error' },
  samdt: { label: 'SAMDT', desc: 'Senate Amendment (S.Amdt.)', color: 'error' },
};

const sortOptions = [
  { value: 'latest_action_desc', label: 'Latest Action (desc)' },
  { value: 'introduced_date_desc', label: 'Introduced Date' },
  { value: 'bill_number_asc', label: 'Bill Number' },
];

// Helper: shallow compare existing URLSearchParams to a plain object of next values
function _paramsEqual(current: URLSearchParams, next: Record<string, any>): boolean {
  try {
    // Normalize: remove keys with empty string/undefined/null
    const cleanNext: Record<string, string> = {};
    Object.keys(next || {}).forEach((k) => {
      const v = next[k];
      if (v === undefined || v === null || (typeof v === 'string' && v.trim() === '')) return;
      cleanNext[k] = String(v);
    });
    // Quick size check
    const curKeys = Array.from(current.keys());
    const uniqueCur = Array.from(new Set(curKeys));
    if (uniqueCur.length !== Object.keys(cleanNext).length) return false;
    // Value check
    for (const k of Object.keys(cleanNext)) {
      if (current.get(k) !== cleanNext[k]) return false;
    }
    // Ensure no extra keys exist in current
    for (const k of uniqueCur) {
      if (!(k in cleanNext)) return false;
    }
    return true;
  } catch {
    return false;
  }
}

const Bills: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { bills, pagination, loading, filters } = useSelector((state: RootState) => state.bills);
  // Safe pagination defaults to avoid runtime errors when API payload is malformed or missing pagination
  const perPage = (pagination && typeof (pagination as any).per_page === 'number') ? (pagination as any).per_page : 50;
  const pageNum = (pagination && typeof (pagination as any).page === 'number') ? (pagination as any).page : 1;

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Local UI state
  const [congress, setCongress] = useState(filters.congress?.toString() || searchParams.get('congress') || '');
  const [billInput, setBillInput] = useState(searchParams.get('bill')?.replace('-', ' ') || '');
  const [types, setTypes] = useState<string[]>((searchParams.get('type') || '').split(',').filter(Boolean));
  const [sort, setSort] = useState<string>(searchParams.get('sort') || 'latest_action_desc');
  const [density, setDensity] = useState<'comfortable' | 'compact'>((searchParams.get('density') as any) || 'comfortable');
  const [searchText, setSearchText] = useState<string>(searchParams.get('q') || '');
  // Quick view from Dashboard: introduced_today, passed_week, votes_now
  const [quickView, setQuickView] = useState<string>(searchParams.get('view') || '');
 
  // Autosuggest state
  const billInputRef = useRef<HTMLInputElement | null>(null);

  // Parse URL on mount into filters
  useEffect(() => {
    const f: any = {};
    const qCongress = searchParams.get('congress');
    if (qCongress) f.congress = parseInt(qCongress);
    const qType = searchParams.get('type');
    if (qType) f.types = qType.split(',').filter(Boolean);
    const qBill = searchParams.get('bill');
    if (qBill) f.bill = qBill;
    const qSort = searchParams.get('sort') || 'latest_action_desc';
    f.sort = qSort;
    const qDensity = (searchParams.get('density') as any) || 'comfortable';
    f.density = qDensity;
    const qText = searchParams.get('q');
    if (qText) f.q = qText;
    const qView = searchParams.get('view');
    if (qView) {
      f.view = qView;
      setQuickView(qView);
    }
    dispatch(setFilters(f));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce auto-apply filters and update URL
  useEffect(() => {
    const handler = setTimeout(() => {
      const f: any = {};
      const sp: any = {};
      if (congress && congress.trim()) {
        f.congress = parseInt(congress);
        sp.congress = parseInt(congress);
      }
      if (types.length) {
        f.types = types;
        sp.type = types.join(',');
      }
      const norm = normalizeBillId(billInput);
      if (norm) {
        const urlBill = formatBillId(norm.type, norm.number, 'url');
        f.bill = urlBill; // HR-1234
        sp.bill = urlBill;
        // recent searches
        try {
          const rs = JSON.parse(localStorage.getItem('recent_bill_searches') || '[]');
          const next = [urlBill, ...rs.filter((x: string) => x !== urlBill)].slice(0, 5);
          localStorage.setItem('recent_bill_searches', JSON.stringify(next));
        } catch {}
      }
      f.sort = sort; sp.sort = sort;
      f.density = density; sp.density = density;
      if (searchText && searchText.trim()) { f.q = searchText.trim(); sp.q = searchText.trim(); } else { delete f.q; }
      if (quickView) { f.view = quickView; sp.view = quickView; }
      dispatch(setFilters(f));
      // Only update the URL when the params actually change to avoid replaceState loops
      if (!_paramsEqual(searchParams, sp)) {
        setSearchParams(sp, { replace: true } as any);
      }
    }, 275);
    return () => clearTimeout(handler);
  }, [congress, billInput, types, sort, density, searchText, quickView, dispatch, setSearchParams, searchParams]);

  // Fetch bills when congress or first selected type changes or pagination page changes
  useEffect(() => {
    const params: any = {
      limit: perPage,
      offset: (pageNum - 1) * perPage,
    };
    if ((filters as any).congress) params.congress = (filters as any).congress;
    // Use server-side type filter only if a single type is selected for efficiency
    const ftypes: string[] = (filters as any).types || [];
    if (ftypes.length === 1) params.billType = ftypes[0];
    if ((filters as any).q) params.q = (filters as any).q;
    if ((filters as any).view) params.view = (filters as any).view;
    dispatch(fetchBills(params));
  }, [dispatch, pageNum, (filters as any).congress, ((filters as any).types || []).join(','), (filters as any).q, (filters as any).view]);

  const handleClearFilters = () => {
    setCongress('');
    setBillInput('');
    setTypes([]);
    setSort('latest_action_desc');
    setDensity('comfortable');
    setSearchText('');
    setSearchParams({});
    dispatch(setFilters({}));
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    const params: any = {
      limit: perPage,
      offset: (page - 1) * perPage,
    };
    if ((filters as any).congress) params.congress = (filters as any).congress;
    const ftypes: string[] = (filters as any).types || [];
    if (ftypes.length === 1) params.billType = ftypes[0];
    if ((filters as any).q) params.q = (filters as any).q;
    if ((filters as any).view) params.view = (filters as any).view;
    dispatch(fetchBills(params));
  };

  const handleBillClick = (bill: any) => {
    const type = bill.bill_type || bill.type;
    const number = bill.bill_number || bill.number;
    navigate(`/bills/${bill.congress}/${type}/${number}`);
  };

  // Visible bills after client-side filtering and sorting
  const visibleBills = useMemo(() => {
    let arr = bills.slice();
    const f: any = filters || {};
    if (f.types && f.types.length) {
      const set = new Set((f.types as string[]).map((t) => t.toLowerCase()));
      arr = arr.filter((b: any) => set.has((b.bill_type || b.type || '').toLowerCase()));
    }
    if (f.bill) {
      const norm = f.bill.toUpperCase(); // e.g., HR-1234
      arr = arr.filter((b: any) => {
        const t = (b.bill_type || b.type || '').toUpperCase();
        const n = (b.bill_number || b.number || '').toString();
        const id = `${t}-${n}`;
        return id === norm;
      });
    }
    // Quick view filters (client-side)
    const view = (f.view || '').toLowerCase();
    if (view) {
      const now = Date.now();
      const isSameLocalDay = (d: any) => {
        const dt = new Date(d);
        const a = new Date();
        return dt.getFullYear() === a.getFullYear() && dt.getMonth() === a.getMonth() && dt.getDate() === a.getDate();
      };
      const withinMs = (d: any, ms: number) => {
        const t = new Date(d).getTime();
        return isFinite(t) && (now - t) <= ms && (now - t) >= 0;
      };
      if (view === 'introduced_today') {
        arr = arr.filter((b: any) => b.introduced_date && isSameLocalDay(b.introduced_date || b.introduced_on));
      } else if (view === 'passed_week') {
        const sevenDays = 7 * 24 * 60 * 60 * 1000;
        arr = arr.filter((b: any) => {
          const lad = b.latest_action_date;
          const la = (b.latest_action || b.latest_action_text || '').toString().toLowerCase();
          const passedLike = (
            la.includes('became law') ||
            la.includes('passed/agreed') ||
            la.includes('agreed to') ||
            la.includes('passed') ||
            la.includes('enacted')
          );
          return passedLike && withinMs(lad, sevenDays);
        });
      } else if (view === 'votes_now') {
        const fifteenMin = 15 * 60 * 1000;
        arr = arr.filter((b: any) => {
          const lad = b.latest_action_date;
          const la = (b.latest_action || '').toString().toLowerCase();
          const voteLike = la.includes('vote') || la.includes('on passage') || la.includes('on agreeing to');
          return voteLike && withinMs(lad, fifteenMin);
        });
      }
    }
    // Sorting
    switch (f.sort) {
      case 'introduced_date_desc':
        arr.sort((a: any, b: any) => new Date(b.introduced_date || b.introduced_on || 0).getTime() - new Date(a.introduced_date || a.introduced_on || 0).getTime());
        break;
      case 'bill_number_asc':
        arr.sort((a: any, b: any) => {
          const at = (a.bill_type || a.type || '').localeCompare(b.bill_type || b.type || '');
          if (at !== 0) return at;
          return Number(a.bill_number || a.number || 0) - Number(b.bill_number || b.number || 0);
        });
        break;
      case 'latest_action_desc':
      default:
        arr.sort((a: any, b: any) => new Date(b.latest_action_date || 0).getTime() - new Date(a.latest_action_date || 0).getTime());
    }
    return arr;
  }, [bills, filters]);

  // Autosuggest options (synthetic): up to 5 progressive matches of current input
  const suggestionOptions = useMemo(() => {
    const norm = normalizeBillId(billInput);
    if (!norm) return [] as string[];
    const full = `${norm.type.toUpperCase()} ${norm.number}`;
    const parts: string[] = [];
    const num = norm.number;
    for (let i = 1; i <= Math.min(num.length, 4); i++) {
      parts.push(`${norm.type.toUpperCase()} ${num.slice(0, i)}`);
    }
    const unique = Array.from(new Set([...parts, full])).slice(0, 5);
    return unique;
  }, [billInput]);

  // SEO: set document title and meta description; add JSON-LD ItemList
  useEffect(() => {
    const parts: string[] = ['Congressional Bills'];
    if ((filters as any)?.congress) parts.push(`Congress ${(filters as any).congress}`);
    if ((filters as any)?.types?.length) parts.push(`Types: ${(filters as any).types.join(', ').toUpperCase()}`);
    if ((filters as any)?.q) parts.push(`Search: ${(filters as any).q}`);
    const title = `${parts.join(' • ')} — AskTheBill.ai`;
    document.title = title;

    const desc = `Browse ${visibleBills.length} bills${(filters as any)?.congress ? ` from the ${ordinal(Number((filters as any).congress))} Congress` : ''}. Filter by type, search text, and sort by introduced date or latest action.`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', desc);

    // JSON-LD for ItemList (limited to 20 items to keep small)
    try {
      const scriptId = 'ld-bills-list';
      const existing = document.getElementById(scriptId);
      const items = visibleBills.slice(0, 20).map((b: any, idx: number) => {
        const t = (b.bill_type || b.type || '').toLowerCase();
        const n = (b.bill_number || b.number || '').toString();
        const url = (b.congress && t && n) ? `${window.location.origin}/bills/${b.congress}/${t}/${n}` : window.location.origin + '/bills';
        return {
          '@type': 'ListItem',
          position: idx + 1,
          url,
          name: `${(t || '').toUpperCase()} ${n} — ${b.title || 'Bill'}`,
        } as any;
      });
      const ld = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: items,
      } as any;
      const json = JSON.stringify(ld);
      if (existing) {
        existing.textContent = json;
      } else {
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.id = scriptId;
        s.text = json;
        document.head.appendChild(s);
      }
    } catch {}
  }, [filters, visibleBills]);

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Congressional Bills
      </Typography>

      {/* Sticky Filters Bar */}
      <Paper sx={{ p: 2, mb: 2, position: 'sticky', top: 64, zIndex: 10 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2.5}>
            <TextField
              fullWidth
              label="Congress Number"
              value={congress}
              onChange={(e) => setCongress(e.target.value.replace(/[^0-9]/g, ''))}
              type="text"
              placeholder="e.g., 118"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            />
          </Grid>
          <Grid item xs={12} md={3.5}>
            <Autocomplete
              freeSolo
              options={suggestionOptions}
              inputValue={billInput}
              onInputChange={(_, value) => setBillInput(value)}
              onChange={(_, value) => {
                if (typeof value === 'string') setBillInput(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={billInputRef}
                  label="Bill Number"
                  placeholder="e.g., H.R. 1234, H.Res. 55, S. 210"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3.5}>
            <TextField
              fullWidth
              label="Search text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Title, summary, sponsor, action, number..."
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="type-multi-label">Bill Types</InputLabel>
              <Select
                labelId="type-multi-label"
                multiple
                value={types}
                label="Bill Types"
                onChange={(e) => setTypes(typeof e.target.value === 'string' ? (e.target.value as string).split(',') : (e.target.value as string[]))}
                renderValue={(selected) => (selected as string[]).map((t) => typeMeta[t]?.label || t.toUpperCase()).join(', ')}
              >
                {Object.keys(typeMeta).map((t) => (
                  <MenuItem key={t} value={t}>
                    <Tooltip title={typeMeta[t].desc} placement="right">
                      <Box component="span">{typeMeta[t].label}</Box>
                    </Tooltip>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel id="sort-label">Sort</InputLabel>
              <Select labelId="sort-label" value={sort} label="Sort" onChange={(e) => setSort(e.target.value)}>
                {sortOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={1}>
            <ToggleButtonGroup
              size="small"
              value={density}
              exclusive
              onChange={(_, val) => val && setDensity(val)}
              aria-label="density"
              fullWidth
            >
              <ToggleButton value="comfortable" aria-label="comfortable">Comf</ToggleButton>
              <ToggleButton value="compact" aria-label="compact">Comp</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={12} md={0.5} textAlign="right">
            <Tooltip title="Clear filters">
              <span>
                <IconButton onClick={handleClearFilters} aria-label="clear filters">
                  <ClearIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
        {/* Active filter chips */}
        <Box mt={1} display="flex" gap={1} flexWrap="wrap">
          {congress && (
            <Chip label={`Congress ${congress}`} onDelete={() => setCongress('')} />
          )}
          {types.map((t) => (
            <Chip key={t} label={typeMeta[t]?.label || t.toUpperCase()} onDelete={() => setTypes(types.filter((x) => x !== t))} />
          ))}
          {normalizeBillId(billInput) && (
            <Chip label={formatBillId(normalizeBillId(billInput)!.type, normalizeBillId(billInput)!.number)} onDelete={() => setBillInput('')} />
          )}
          {searchText && searchText.trim() && (
            <Chip label={`Search: ${searchText.trim()}`} onDelete={() => setSearchText('')} />
          )}
          {quickView && quickView.toLowerCase() === 'introduced_today' && (
            <Chip color="primary" label="Introduced Today" onDelete={() => setQuickView('')} />
          )}
          {quickView && quickView.toLowerCase() === 'passed_week' && (
            <Chip color="success" label="Passed This Week" onDelete={() => setQuickView('')} />
          )}
          {quickView && quickView.toLowerCase() === 'votes_now' && (
            <Chip color="warning" label="Votes Happening Now" onDelete={() => setQuickView('')} />
          )}
        </Box>
      </Paper>

      {loading ? (
        <LoadingSpinner message="Loading bills..." />
      ) : (
        <>
          {/* Bills Grid */}
          {visibleBills.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>No results</Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>Try adjusting your filters or clearing them.</Typography>
              <Button variant="outlined" onClick={handleClearFilters}>Clear filters</Button>
            </Paper>
          ) : (
            <>
              <Box display="flex" justifyContent="flex-end" mb={1}>
                <ExportButtons
                  filename={`bills-${(filters as any).congress || 'all'}`}
                  data={visibleBills.map((b: any) => ({
                    congress: b.congress,
                    type: b.bill_type || b.type,
                    number: b.bill_number || b.number,
                    title: b.title,
                    sponsor: b.sponsor_name || b.sponsor,
                    introduced_date: b.introduced_date || b.introduced_on || '',
                    latest_action_date: b.latest_action_date,
                  }))}
                />
              </Box>
              <Grid container spacing={2}>
                {visibleBills.map((bill: any, index: number) => {
                const t = (bill.bill_type || bill.type || '').toLowerCase();
                const n = (bill.bill_number || bill.number || '').toString();
                const meta = typeMeta[t] || { label: (t || '').toUpperCase(), color: 'default', desc: '' } as any;
                const idDisplay = `${meta.label} ${n}`;
                const detailPath = (bill.congress && t && n) ? `/bills/${bill.congress}/${t}/${n}` : '';
                const introducedRaw = bill.introduced_date || bill.introduced_on || '';
                const introducedPretty = introducedRaw ? new Date(introducedRaw).toLocaleDateString() : '';
                return (
                  <Grid item xs={12} md={6} lg={4} key={index}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1, pb: density === 'compact' ? 1 : 2 }}>
                        {/* Prominent Bill ID */}
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Chip label={meta.label} color={meta.color} size="small" />
                          <Typography variant="h6" component="h2" fontWeight="bold">{idDisplay}{bill.congress ? ` (${ordinal(bill.congress)})` : ''}</Typography>
                        </Box>
                        {/* Title (optional) */}
                        {bill.title && density === 'comfortable' && (
                          <Typography variant="subtitle1" gutterBottom>
                            {detailPath ? (
                              <Box component={RouterLink as any} to={detailPath} color="inherit" sx={{ textDecoration: 'none' }} aria-label={`${idDisplay}: ${bill.title}`}>
                                {bill.title}
                              </Box>
                            ) : (
                              bill.title
                            )}
                          </Typography>
                        )}
                        {/* Secondary metadata row */}
                        <Typography variant="body2" color="text.secondary">
                          {(bill.sponsor_name || bill.sponsor || 'Unknown Sponsor')}
                          {bill.committees ? ` • ${bill.committees}` : ''}
                          {bill.latest_action_date ? ` • ${new Date(bill.latest_action_date).toLocaleDateString()}` : ''}
                        </Typography>
                        {/* Introduced date row */}
                        {introducedPretty && (
                          <Typography variant="caption" color="text.secondary" mt={0.5} component="div">
                            Introduced: <time dateTime={introducedRaw}>{introducedPretty}</time>
                          </Typography>
                        )}
                        {density === 'comfortable' && bill.latest_action_text && (
                          <Typography variant="body2" color="text.secondary" mt={1}>
                            {bill.latest_action_text}
                          </Typography>
                        )}
                      </CardContent>
                      <CardActions sx={{ pt: 0 }}>
                        {detailPath ? (
                          <Button size="small" component={RouterLink as any} to={detailPath} aria-label={`View details for ${idDisplay}${bill.title ? `: ${bill.title}` : ''}`}>
                            View Details
                          </Button>
                        ) : (
                          <Button size="small" onClick={() => handleBillClick(bill)} disabled={!(t && n)}>
                            View Details
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            if (!(t && n)) return;
                            navigate('/chat', { state: { billContext: [`${bill.congress}:${t}:${n}`] } });
                          }}
                          disabled={!(t && n)}
                          aria-label={`Chat about ${idDisplay}${bill.title ? `: ${bill.title}` : ''}`}
                        >
                          Chat with Bill
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
            </>
          )}

          {/* Pagination */}
          {(Number((pagination as any)?.total || 0) > perPage) && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={Math.ceil(Number((pagination as any)?.total || 0) / perPage)}
                page={pageNum}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default Bills;
